import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setCarTripSearchInputs, setCarTripSearchParam } from '../../../../../store/slices/tripSearch';
import CheckboxSelectFilter from '../../../../Filters/CheckboxSelectFilter';
import carSearchInput from '../../../../../helpers/selectors/carSearchInput';
import { carSearch } from '../../../../../store/slices/car';
import { checkFoldMultipleRateCodes } from '../../../../../store/slices/user';

const RateCodePreferencesFilter = ({
  tripId, selectedTab, variant, onChange, bgClassName,
}) => {
  const isTripSearch = !tripId;
  const {
    searchInputs, searchObj,
  } = useSelector(carSearchInput(tripId, selectedTab, isTripSearch));
  const { rentalCarDiscountCodes, travelAgency, isLoggedIn } = useSelector((store) => store.user);
  const [options, setOptions] = useState([]);
  const dispatch = useDispatch();
  const doShowRateCodesFoldedIntoGroupInLabel = travelAgency && travelAgency.settings
   && travelAgency.settings.doShowRateCodesFoldedIntoGroupInLabel;

  useEffect(() => {
    if (rentalCarDiscountCodes && rentalCarDiscountCodes.length) {
      let isOptionsSet = false;
      if (checkFoldMultipleRateCodes(isLoggedIn, travelAgency)) {
        const rateCodesWithGroup = _.filter(rentalCarDiscountCodes, (r) => r.rateGroup
      && r.rateGroup.groupName);

        if (rateCodesWithGroup.length) {
          const carRateGroups = _.groupBy(rateCodesWithGroup, (r) => r.rateGroup.groupName);
          const getLabel = (key, rateGroup) => {
            let label = `${key}`;
            if (doShowRateCodesFoldedIntoGroupInLabel) {
              const membershipId = _.map(rateGroup, (r) => `${r.vendorName} (${r.membershipId})`).join(', ');
              label += ` - ${membershipId}`;
            }
            return label;
          };

          const rates = _.map(carRateGroups, (rateGroup, key) => ({
            value: _.map(rateGroup, 'id').join(','),
            label: getLabel(key, rateGroup),
            rateCodes: _.map(rateGroup, 'id'),
          }));
          setOptions(rates);
          isOptionsSet = true;
        }
      }
      if (!isOptionsSet) {
        setOptions(rentalCarDiscountCodes.map((r) => ({ value: r.id, label: r.label })));
      }
    }
  }, [doShowRateCodesFoldedIntoGroupInLabel, isLoggedIn, rentalCarDiscountCodes, travelAgency]);

  const selectedValues = useMemo(() => {
    const selectedRates = (searchInputs && searchInputs.rateCodes) || [];
    if (selectedRates.length && _.find(options, (o) => o.rateCodes && o.rateCodes.length)) {
      return _.map(_.filter(options, (o) => o.rateCodes && _.find(selectedRates, (r) => o.rateCodes.indexOf(r) > -1)), 'value');
    }
    return selectedRates;
  }, [searchInputs, options]);
  const handleChange = useCallback((newValue, isPreferredCarFilterNewVal) => {
    const payload = {
      searchInputs: {
      },
    };
    if (onChange) {
      if (typeof newValue === 'object' && !Array.isArray(newValue)) {
        onChange(newValue);
      } else {
        onChange({
          rateCodes: [...newValue],
        });
      }
      return;
    }
    if (isTripSearch) {
      if (isPreferredCarFilterNewVal) {
        payload.searchInputs.rateCodes = _.flatMap(newValue, (r) => r?.split(','));
        dispatch(setCarTripSearchInputs({ ...payload.searchInputs }));
      } else {
        dispatch(setCarTripSearchInputs(newValue));
      }
    } else {
      dispatch(carSearch({
        tripId,
        selectedTab,
        searchObj: {
          ...searchInputs,
          page: 1,
          ...newValue,
        },
      }));
    }
  }, [onChange, isTripSearch, dispatch, tripId, selectedTab, searchInputs]);

  useEffect(() => {
    if ((travelAgency
|| (rentalCarDiscountCodes && rentalCarDiscountCodes.length > 0))
        && searchInputs.rateCodes === undefined) {
      let defaultSelection = _.filter(rentalCarDiscountCodes, (r) => r.isSelectedByDefault);
      if (travelAgency.carDirectBillingVendors && travelAgency.carDirectBillingVendors.length) {
        defaultSelection = _.union(defaultSelection, travelAgency.carDirectBillingVendors);
        defaultSelection = _.uniqBy(defaultSelection, 'vendorCode');
      }
      if (defaultSelection.length > 0) {
        const obj = {};
        obj.rateCodes = _.map(defaultSelection, 'id');
        if (isTripSearch && travelAgency && travelAgency.defaultSetRateCodeCarVendorAsFilter) {
          obj.rentalCarAgencies = _.uniq(_.map(defaultSelection, 'vendorCode'));
          dispatch(setCarTripSearchParam({
            responseFilters: {
              ...((searchObj || {}).responseFilters || {}),
              rentalAgencyResults: _.map(defaultSelection,
                (r) => ({ value: r.vendorCode, label: r.vendorName })),
            },
          }));
        }
        handleChange(obj);
      }
    }
  }, [handleChange, rentalCarDiscountCodes, searchInputs.rateCodes, travelAgency,
    isTripSearch, searchObj, dispatch]);

  return options
    && options.length
    && (!travelAgency || !travelAgency.settings
      || travelAgency.settings.doShowCarRentalRateCodesFilter)
    ? (
      <CheckboxSelectFilter
        options={options}
        values={selectedValues || []}
        onChange={(newValue) => handleChange(newValue, true)}
        label={(travelAgency && travelAgency.filterSettings && travelAgency.filterSettings.rentalCarRatesFilterLabel) || 'Rate Codes'}
        selectedValuePrefix={travelAgency && travelAgency.filterSettings && travelAgency.filterSettings.rentalCarRatesFilterLabel ? `${travelAgency.filterSettings.rentalCarRatesFilterLabel}:` : 'Preferred Rate Codes:'}
        widthInside="max-w-85"
        menuWidthClass={doShowRateCodesFoldedIntoGroupInLabel ? 'w-full min-w-56 md:w-108' : 'w-full md:w-max'}
        filterMaxWidthClass="max-w-60"
        variant={variant}
        bgClassName={!isTripSearch ? '' : bgClassName}
      />
    ) : <></>;
};

RateCodePreferencesFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  bgClassName: PropTypes.string,
  selectedTab: PropTypes.number.isRequired,
  onChange: PropTypes.func,
};

RateCodePreferencesFilter.defaultProps = {
  tripId: undefined,
  bgClassName: undefined,
  variant: 'outline',
  onChange: undefined,
};

export default RateCodePreferencesFilter;
